import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { qk, useWorkflow } from "../../api/queries";
import type { Gate, GateType, Stage, StageKind } from "../../api/types";
import { useDialogs } from "../../components/Dialogs";
import { Icon } from "../../components/Icon";
import { Pill } from "../../components/Pill";
import { RelativeTime } from "../../components/RelativeTime";
import { ErrorBox, Spinner } from "../../components/Spinner";
import { GATE_COLOR, GATE_LABEL, STAGE_KIND_LABEL } from "../../lib/status";

const KINDS: StageKind[] = ["human", "agent", "integration", "done"];
const GATE_TYPES: GateType[] = ["ai_review", "cross_task_review", "human_review"];

function newStage(existing: Stage[]): Stage {
  let n = existing.length + 1;
  while (existing.some((s) => s.key === `stage_${n}`)) n++;
  return {
    key: `stage_${n}`,
    name: "New stage",
    kind: "agent",
    prompt: "",
    allowed_agent_types: [],
    gates: [],
    next: null,
    outputs: "",
  };
}

function newGate(type: GateType): Gate {
  return type === "human_review"
    ? { type, name: "Human review", blocking: true }
    : type === "cross_task_review"
      ? { type, name: "Cross-task review", prompt: "", blocking: false }
      : { type, name: "AI review", prompt: "", blocking: true, max_retries: 2 };
}

export function WorkflowSettings({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { toast } = useDialogs();
  const workflow = useWorkflow(pid);
  const versions = useQuery({ queryKey: qk.workflowVersions(pid), queryFn: () => api.workflowVersions(pid) });
  const pool = useQuery({ queryKey: qk.agentPool, queryFn: api.agentPool, retry: false });
  const [stages, setStages] = useState<Stage[]>([]);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    if (workflow.data) setStages(structuredClone(workflow.data.stages));
  }, [workflow.data]);

  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.putWorkflow(pid, stages),
    onSuccess: (w) => {
      qc.invalidateQueries({ queryKey: qk.workflow(pid) });
      qc.invalidateQueries({ queryKey: qk.workflowVersions(pid) });
      toast(`Saved workflow v${w.version}`, "info");
    },
  });

  if (workflow.isLoading) return <Spinner />;
  if (workflow.error) return <ErrorBox error={workflow.error} />;

  const dirty = JSON.stringify(stages) !== JSON.stringify(workflow.data?.stages ?? []);
  const agentTypes = pool.data?.types.map((t) => t.agent_type) ?? ["claude", "codex", "mock"];

  const update = (i: number, patch: Partial<Stage>) =>
    setStages((ss) => ss.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const moveStage = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= stages.length) return;
    const out = [...stages];
    [out[i], out[j]] = [out[j], out[i]];
    setStages(out);
    setOpen((o) => (o === i ? j : o === j ? i : o));
  };

  return (
    <div className="settings-split">
      <div className="settings-main">
        <div className="info-box">
          Saving creates a new workflow version. Only tasks created afterwards use it; in-flight tasks stay pinned to
          the version they were created with.
        </div>
        <ol className="stage-list">
          {stages.map((s, i) => (
            <li key={i} className="stage-card">
              <div className="stage-card-header">
                <button className="stage-toggle" onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i}>
                  <Icon name={open === i ? "chevronDown" : "chevronRight"} size={14} />
                  <span className="stage-index muted">{i + 1}</span>
                  <strong>{s.name || s.key}</strong>
                  <Pill color="gray">{STAGE_KIND_LABEL[s.kind]}</Pill>
                  {s.gates.map((g, gi) => (
                    <Pill key={gi} color={GATE_COLOR[g.type]}>
                      {g.name || GATE_LABEL[g.type]}
                    </Pill>
                  ))}
                </button>
                <span className="spacer" />
                <button className="btn-icon" onClick={() => moveStage(i, -1)} disabled={i === 0} aria-label="Move up">
                  <Icon name="up" size={14} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => moveStage(i, 1)}
                  disabled={i === stages.length - 1}
                  aria-label="Move down"
                >
                  <Icon name="down" size={14} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => {
                    setStages((ss) => ss.filter((_, j) => j !== i));
                    setOpen(null);
                  }}
                  aria-label="Remove stage"
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
              {open === i && (
                <StageEditor stage={s} stages={stages} agentTypes={agentTypes} onChange={(p) => update(i, p)} />
              )}
            </li>
          ))}
        </ol>
        <button
          className="btn"
          onClick={() => {
            setStages((ss) => [...ss, newStage(ss)]);
            setOpen(stages.length);
          }}
        >
          <Icon name="plus" size={14} /> Add stage
        </button>
        {save.error && <div className="error-box">{save.error.message}</div>}
        <div className="row-actions row-actions-start sticky-actions">
          <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save as new version"}
          </button>
          {dirty && (
            <button className="btn" onClick={() => setStages(structuredClone(workflow.data!.stages))}>
              Discard changes
            </button>
          )}
          <span className="muted small">Active: v{workflow.data?.version}</span>
        </div>
      </div>
      <aside className="settings-aside">
        <h3 className="section-label">Versions</h3>
        {versions.isLoading && <Spinner />}
        <ul className="version-list">
          {versions.data?.map((v) => (
            <li key={v.id}>
              <div className="version-item">
                <strong>v{v.version}</strong>
                {v.id === workflow.data?.id && <Pill color="blue">active</Pill>}
                <span className="muted small">
                  {v.stages.length} stages · <RelativeTime iso={v.created_at} />
                </span>
                {v.id !== workflow.data?.id && (
                  <button className="btn btn-sm" onClick={() => setStages(structuredClone(v.stages))}>
                    Load
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

function StageEditor({
  stage,
  stages,
  agentTypes,
  onChange,
}: {
  stage: Stage;
  stages: Stage[];
  agentTypes: string[];
  onChange: (p: Partial<Stage>) => void;
}) {
  const others = stages.filter((s) => s !== stage);
  const allTypes = Array.from(new Set([...agentTypes, ...stage.allowed_agent_types]));
  const updateGate = (i: number, p: Partial<Gate>) =>
    onChange({ gates: stage.gates.map((g, j) => (j === i ? { ...g, ...p } : g)) });

  return (
    <div className="stage-editor form-stack">
      <div className="field-row">
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="input"
            value={stage.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field-label">Key</span>
          <input
            className="input input-mono"
            value={stage.key}
            onChange={(e) => onChange({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
          />
        </label>
        <label className="field">
          <span className="field-label">Kind</span>
          <select className="input" value={stage.kind} onChange={(e) => onChange({ kind: e.target.value as StageKind })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {STAGE_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="field-label">Instruction prompt</span>
        <textarea
          className="input"
          rows={6}
          value={stage.prompt}
          onChange={(e) => onChange({ prompt: e.target.value })}
          placeholder="What the agent should do in this stage"
        />
      </label>
      <label className="field">
        <span className="field-label">Expected outputs</span>
        <textarea className="input" rows={2} value={stage.outputs} onChange={(e) => onChange({ outputs: e.target.value })} />
      </label>
      <div className="field">
        <span className="field-label">Allowed agent types</span>
        <div className="checkbox-list">
          {allTypes.map((t) => (
            <label key={t} className="checkbox">
              <input
                type="checkbox"
                checked={stage.allowed_agent_types.includes(t)}
                onChange={(e) =>
                  onChange({
                    allowed_agent_types: e.target.checked
                      ? [...stage.allowed_agent_types, t]
                      : stage.allowed_agent_types.filter((x) => x !== t),
                  })
                }
              />
              {t}
            </label>
          ))}
        </div>
        <span className="field-help muted">In order of preference. Ignored for human stages.</span>
      </div>
      <div className="field-row">
        <label className="field">
          <span className="field-label">Next stage</span>
          <select
            className="input"
            value={stage.next ?? ""}
            onChange={(e) => onChange({ next: e.target.value || null })}
          >
            <option value="">Next in order</option>
            {others.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        {stage.kind === "integration" && (
          <label className="field">
            <span className="field-label">On CI / merge failure send back to</span>
            <select
              className="input"
              value={stage.send_back_on_failure ?? ""}
              onChange={(e) => onChange({ send_back_on_failure: e.target.value || null })}
            >
              <option value="">Previous agent stage</option>
              {others.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="field">
        <span className="field-label">Gates (evaluated in order)</span>
        {stage.gates.map((g, i) => (
          <div key={i} className="gate-editor">
            <div className="field-row">
              <label className="field">
                <span className="field-label">Type</span>
                <select
                  className="input"
                  value={g.type}
                  onChange={(e) => updateGate(i, { ...newGate(e.target.value as GateType), name: g.name })}
                >
                  {GATE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {GATE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Name</span>
                <input className="input" value={g.name} onChange={(e) => updateGate(i, { name: e.target.value })} />
              </label>
              {g.type !== "human_review" && (
                <label className="field">
                  <span className="field-label">Agent type</span>
                  <select
                    className="input"
                    value={g.agent_type ?? ""}
                    onChange={(e) => updateGate(i, { agent_type: e.target.value || undefined })}
                  >
                    <option value="">Any allowed</option>
                    {agentTypes.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {g.type !== "human_review" && (
              <>
                <label className="field">
                  <span className="field-label">Review prompt</span>
                  <textarea
                    className="input"
                    rows={3}
                    value={g.prompt ?? ""}
                    onChange={(e) => updateGate(i, { prompt: e.target.value })}
                  />
                </label>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={!!g.parallel}
                    onChange={(e) => updateGate(i, { parallel: e.target.checked || undefined })}
                  />
                  Run alongside the next human review
                </label>
                {g.parallel && (
                  <p className="field-help muted">
                    The human review starts right away; this review's findings appear in it when ready and never send the
                    task back by themselves. Deciding the human review first cancels it.
                    {!stage.gates.slice(i + 1).some((n) => n.type === "human_review") && (
                      <span className="error-text"> Add a human review gate after this one.</span>
                    )}
                  </p>
                )}
                <div className="field-row">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={g.blocking}
                      disabled={!!g.parallel}
                      onChange={(e) => updateGate(i, { blocking: e.target.checked })}
                    />
                    Blocking (a fail sends the task back)
                  </label>
                  <label className="field field-inline">
                    <span className="field-label">Max retries</span>
                    <input
                      className="input input-num"
                      type="number"
                      min={0}
                      value={g.max_retries ?? ""}
                      onChange={(e) =>
                        updateGate(i, { max_retries: e.target.value === "" ? undefined : Number(e.target.value) })
                      }
                    />
                  </label>
                </div>
              </>
            )}
            <div className="row-actions">
              <button
                className="btn btn-sm"
                disabled={i === 0}
                onClick={() => {
                  const gs = [...stage.gates];
                  [gs[i - 1], gs[i]] = [gs[i], gs[i - 1]];
                  onChange({ gates: gs });
                }}
              >
                Move up
              </button>
              <button
                className="btn btn-sm btn-ghost-danger"
                onClick={() => onChange({ gates: stage.gates.filter((_, j) => j !== i) })}
              >
                Remove gate
              </button>
            </div>
          </div>
        ))}
        <div className="row-actions row-actions-start">
          {GATE_TYPES.map((t) => (
            <button key={t} className="btn btn-sm" onClick={() => onChange({ gates: [...stage.gates, newGate(t)] })}>
              <Icon name="plus" size={12} /> {GATE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
